import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import React from 'react'
import { Badge, Tag } from 'antd'
import { DataType } from './interface'
import { Stage } from '../../../components/activity-timeline/styles'
import { formatPrice } from '../../utils'
import { Value } from '../../elements'
import { Sorter } from '../../../interfaces'
import { RowActionMenu } from '../../../components/row-action-menu'
import { formatPhoneNumber } from '../../../../lib/utils/helpers'
import { NotesCell } from '../../../components/v2/notes-cell'
import { StageData } from 'src/react-app/components/v2/activity-timeline'

const ColumnStage: ColumnsType<DataType> = [
  {
    title: 'Стадия',
    dataIndex: 'stage',
    key: 'stage',
    width: 130,
    fixed: 'left',
    render: (_, { stage }) => <StageContainer stage={stage} />,
  },
  {
    title: 'ID',
    dataIndex: 'parent_id',
    key: 'parent_id',
    width: 100,
    render: (_, { parent_id, repeat_number }) => (
      <>
        {parent_id}
        <Badge
          color="rgb(73, 110, 128)"
          count={repeat_number}
          size="small"
          offset={[5, -5]}
          title={`Повторное обращение №${repeat_number}`}
        />
      </>
    ),
  },
  {
    title: 'Заметки',
    dataIndex: 'notes_count',
    key: 'notes_count',
    width: 80,
    align: 'center',
    render: (count, { parent_id }) => <NotesCell cardId={parent_id} count={count} />,
  },
]

const ColumnBrand: ColumnsType<DataType> = [
  {
    title: 'Бренд',
    width: 150,
    dataIndex: 'brand',
    key: 'brand',
    sorter: true,
    render: val => <Value data={val} />,
  },
]
const ColumnOrganization: ColumnsType<DataType> = [
  {
    title: 'Офис',
    width: 200,
    dataIndex: 'office',
    key: 'office',
    sorter: true,
    render: val => <Value data={val} />,
  },
]

const ColumnsGeneral: ColumnsType<DataType> = [
  {
    title: 'Дата обращения',
    dataIndex: 'appeal_date',
    key: 'appeal_date',
    width: 130,
    sorter: true,
    render: val => dayjs(val).format('DD.MM.YYYY HH:mm'),
  },
  {
    title: 'ФИО менеджера',
    dataIndex: 'manager_name',
    key: 'manager_name',
    width: 120,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата замера (план)',
    dataIndex: 'measurement_date',
    key: 'measurement_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
  },
  {
    title: 'Время замера (план)',
    dataIndex: 'measurement_time',
    key: 'measurement_time',
    width: 130,
    render: (values?: string[]) => {
      if (values && Array.isArray(values) && values?.length) {
        return (
          <>
            {values.map(time => (
              <Tag key={time}>{time}</Tag>
            ))}
          </>
        )
      }
      return <Value data={values} />
    },
  },
  {
    title: 'ФИО ответственного за замер',
    dataIndex: 'responsible',
    key: 'responsible',
    width: 130,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Адрес замера',
    dataIndex: 'address',
    key: 'address',
    width: 120,
    render: val => <Value data={val} />,
  },
  {
    title: 'Примечание к адресу',
    dataIndex: 'address_note',
    key: 'address_note',
    width: 140,
    render: val => <Value data={val} />,
  },
  {
    title: 'Телефон клиента',
    dataIndex: 'phone',
    key: 'phone',
    width: 150,
    render: val => formatPhoneNumber(val),
  },
  {
    title: 'ФИО клиента',
    dataIndex: 'name',
    key: 'name',
    width: 160,
  },
  {
    title: 'Доп телефон клиента',
    dataIndex: 'additional_phone',
    key: 'additional_phone',
    width: 120,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Сумма заказа',
    dataIndex: 'case_total',
    key: 'case_total',
    width: 130,
    sorter: true,
    render: key => formatPrice(key || 0),
  },
  {
    title: 'Признак заказа',
    dataIndex: 'order_attribute',
    key: 'order_attribute',
    width: 180,
    render: val => <Value data={val} />,
  },
  {
    title: 'Номер производственного заказа',
    dataIndex: 'production_number',
    key: 'production_number',
    width: 180,
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата замера (скорр)',
    dataIndex: 'date_adjusted',
    key: 'date_adjusted',
    width: 180,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : <Value data={val} />),
  },
  {
    title: 'Время замера (скорр)',
    dataIndex: 'time_adjusted',
    key: 'time_adjusted',
    width: 180,
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата и время замера (факт)',
    dataIndex: 'measurement_datetime',
    key: 'measurement_datetime',
    width: 180,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
  },
  {
    title: 'Дата создания',
    width: 200,
    dataIndex: 'created_at',
    key: 'created_at',
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
  },
  {
    title: 'Дата обновления',
    width: 200,
    dataIndex: 'updated_at',
    key: 'updated_at',
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : <Value data={val} />),
  },
]

const ColumnAction: ColumnsType<DataType> = [
  {
    title: '',
    key: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 50,
    align: 'center',
    render: (_, { id, deleteRecord }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <RowActionMenu id={Number(id)} deleteRecord={deleteRecord} />
        </span>
      )
    },
  },
]

export function getColumns(leader: boolean, measurer: boolean, sorter: Sorter) {
  const columns: ColumnsType<DataType> = []
  if (measurer) {
    columns.push(...ColumnBrand)
  }
  columns.push(...ColumnStage)
  if (leader) {
    columns.push(...ColumnOrganization)
  }
  columns.push(...ColumnsGeneral)
  if (leader) {
    columns.push(...ColumnAction)
  }
  return columns.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order
    } else {
      delete item.sortOrder
    }
    return item
  })
}

export function StageContainer({ stage }: { stage: StageData }) {
  if (stage) {
    return <Stage style={stage?.style}>{stage.name}</Stage>
  }
  return <>...</>
}
